import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';

// Department Submission Service for handling file uploads
export const departmentSubmissionService = {
  
  /**
   * Upload a PDF file for a department
   */
  async uploadSubmission(file, department, financialYear, userId, userName) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }
      
      // 10 MB limit
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10 MB');
      }
      
      // Check if there's an existing unlocked submission
      const existingSubmission = await this.getSubmissionByDepartment(department, financialYear);
      
      if (existingSubmission && existingSubmission.isLocked) {
        throw new Error('You have already locked your submission. Please contact admin to make changes.');
      }
      
      // Generate file name
      const timestamp = Date.now();
      const sanitizedUserName = userName.replace(/[^a-zA-Z0-9]/g, '_');
      const sanitizedDepartment = department.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${sanitizedDepartment}_FY_${financialYear}_${sanitizedUserName}_${timestamp}.pdf`;
      
      // Create storage reference
      const filePath = `submissions/${financialYear}/${department}/${fileName}`;
      const storageRef = ref(storage, filePath);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(uploadResult.ref);
      
      // If there's an existing submission, delete the old file
      if (existingSubmission && existingSubmission.filePath) {
        try {
          const oldFileRef = ref(storage, existingSubmission.filePath);
          await deleteObject(oldFileRef);
        } catch (error) {
          console.warn('Could not delete old file:', error);
        }
      }
      
      // Prepare submission data
      const submissionData = {
        department,
        financialYear,
        uploadedBy: userId,
        uploadedByName: userName,
        fileName,
        fileUrl,
        filePath,
        fileSize: file.size,
        uploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isLocked: false,
        lockedAt: null,
        lockedBy: null,
        status: 'draft'
      };
      
      // Save or update submission record
      if (existingSubmission) {
        // Update existing submission
        const submissionRef = doc(db, 'departmentSubmissions', existingSubmission.id);
        await updateDoc(submissionRef, submissionData);
        
        return { 
          success: true, 
          data: { id: existingSubmission.id, ...submissionData },
          message: 'File updated successfully!'
        };
      } else {
        // Create new submission
        const docRef = await addDoc(collection(db, 'departmentSubmissions'), submissionData);
        
        return { 
          success: true, 
          data: { id: docRef.id, ...submissionData },
          message: 'File uploaded successfully!'
        };
      }
    } catch (error) {
      console.error('Error uploading submission:', error);
      throw new Error(error.message || 'Failed to upload file. Please try again.');
    }
  },

  /**
   * Get submission by department and financial year
   */
  async getSubmissionByDepartment(department, financialYear) {
    try {
      const q = query(
        collection(db, 'departmentSubmissions'),
        where('department', '==', department),
        where('financialYear', '==', financialYear)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        lockedAt: doc.data().lockedAt?.toDate()
      };
    } catch (error) {
      console.error('Error getting submission:', error);
      throw new Error('Failed to load submission data');
    }
  },

  /**
   * Get all submissions (for admin)
   */
  async getAllSubmissions(financialYear = null) {
    try {
      let q;
      
      if (financialYear) {
        q = query(
          collection(db, 'departmentSubmissions'),
          where('financialYear', '==', financialYear),
          orderBy('updatedAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'departmentSubmissions'),
          orderBy('updatedAt', 'desc')
        );
      }
      
      const querySnapshot = await getDocs(q);
      const submissions = [];
      
      querySnapshot.forEach((doc) => {
        submissions.push({
          id: doc.id,
          ...doc.data(),
          uploadedAt: doc.data().uploadedAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
          lockedAt: doc.data().lockedAt?.toDate()
        });
      });
      
      return { success: true, data: submissions };
    } catch (error) {
      console.error('Error getting all submissions:', error);
      throw new Error('Failed to load submissions');
    }
  },

  /**
   * NEW: Get list of departments with locked submissions for a financial year
   */
  async getLockedDepartments(financialYear) {
    try {
      const q = query(
        collection(db, 'departmentSubmissions'),
        where('financialYear', '==', financialYear),
        where('isLocked', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const departments = [];
      
      querySnapshot.forEach((doc) => {
        departments.push(doc.data().department);
      });
      
      return { success: true, data: departments };
    } catch (error) {
      console.error('Error getting locked departments:', error);
      throw new Error('Failed to load locked departments');
    }
  },

  /**
   * Lock a submission (final submit)
   */
  async lockSubmission(submissionId, userId, userName) {
    try {
      const submissionRef = doc(db, 'departmentSubmissions', submissionId);
      const submissionDoc = await getDoc(submissionRef);
      
      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }
      
      const submission = submissionDoc.data();
      
      if (submission.isLocked) {
        throw new Error('This submission is already locked');
      }
      
      await updateDoc(submissionRef, {
        isLocked: true,
        lockedAt: serverTimestamp(),
        lockedBy: userId,
        lockedByName: userName,
        status: 'locked',
        updatedAt: serverTimestamp()
      });
      
      return { 
        success: true, 
        message: 'Submission locked successfully! You can no longer make changes.'
      };
    } catch (error) {
      console.error('Error locking submission:', error);
      throw new Error(error.message || 'Failed to lock submission');
    }
  },

  /**
   * Unlock a submission (admin only)
   */
  async unlockSubmission(submissionId, adminId, adminName) {
    try {
      const submissionRef = doc(db, 'departmentSubmissions', submissionId);
      const submissionDoc = await getDoc(submissionRef);
      
      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }
      
      await updateDoc(submissionRef, {
        isLocked: false,
        lockedAt: null,
        lockedBy: null,
        lockedByName: null,
        status: 'draft',
        updatedAt: serverTimestamp(),
        unlockedBy: adminId,
        unlockedByName: adminName,
        unlockedAt: serverTimestamp()
      });
      
      return { 
        success: true, 
        message: 'Submission unlocked successfully! Department can now make changes.'
      };
    } catch (error) {
      console.error('Error unlocking submission:', error);
      throw new Error(error.message || 'Failed to unlock submission');
    }
  },

  /**
   * Delete a submission (remove file and record)
   */
  async deleteSubmission(submissionId) {
    try {
      const submissionRef = doc(db, 'departmentSubmissions', submissionId);
      const submissionDoc = await getDoc(submissionRef);
      
      if (!submissionDoc.exists()) {
        throw new Error('Submission not found');
      }
      
      const submission = submissionDoc.data();
      
      // Delete file from storage
      if (submission.filePath) {
        try {
          const fileRef = ref(storage, submission.filePath);
          await deleteObject(fileRef);
        } catch (error) {
          console.warn('Could not delete file from storage:', error);
        }
      }
      
      // Delete Firestore record
      await deleteDoc(submissionRef);
      
      return { 
        success: true, 
        message: 'Submission deleted successfully!'
      };
    } catch (error) {
      console.error('Error deleting submission:', error);
      throw new Error(error.message || 'Failed to delete submission');
    }
  },

  /**
   * Admin upload on behalf of department
   */
  async adminUploadForDepartment(file, department, financialYear, adminId, adminName) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }
      
      // 10 MB limit
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10 MB');
      }
      
      // Check if there's an existing submission
      const existingSubmission = await this.getSubmissionByDepartment(department, financialYear);
      
      // Generate file name
      const timestamp = Date.now();
      const sanitizedAdminName = adminName.replace(/[^a-zA-Z0-9]/g, '_');
      const sanitizedDepartment = department.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${sanitizedDepartment}_FY_${financialYear}_Admin_${sanitizedAdminName}_${timestamp}.pdf`;
      
      // Create storage reference
      const filePath = `submissions/${financialYear}/${department}/${fileName}`;
      const storageRef = ref(storage, filePath);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(uploadResult.ref);
      
      // If there's an existing submission, delete the old file
      if (existingSubmission && existingSubmission.filePath) {
        try {
          const oldFileRef = ref(storage, existingSubmission.filePath);
          await deleteObject(oldFileRef);
        } catch (error) {
          console.warn('Could not delete old file:', error);
        }
      }
      
      // Prepare submission data
      const submissionData = {
        department,
        financialYear,
        uploadedBy: adminId,
        uploadedByName: `${adminName} (Admin)`,
        fileName,
        fileUrl,
        filePath,
        fileSize: file.size,
        uploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isLocked: existingSubmission ? existingSubmission.isLocked : false,
        lockedAt: existingSubmission ? existingSubmission.lockedAt : null,
        lockedBy: existingSubmission ? existingSubmission.lockedBy : null,
        status: existingSubmission ? existingSubmission.status : 'draft',
        uploadedByAdmin: true
      };
      
      // Save or update submission record
      if (existingSubmission) {
        // Update existing submission
        const submissionRef = doc(db, 'departmentSubmissions', existingSubmission.id);
        await updateDoc(submissionRef, submissionData);
        
        return { 
          success: true, 
          data: { id: existingSubmission.id, ...submissionData },
          message: 'File uploaded successfully on behalf of department!'
        };
      } else {
        // Create new submission
        const docRef = await addDoc(collection(db, 'departmentSubmissions'), submissionData);
        
        return { 
          success: true, 
          data: { id: docRef.id, ...submissionData },
          message: 'File uploaded successfully on behalf of department!'
        };
      }
    } catch (error) {
      console.error('Error in admin upload:', error);
      throw new Error(error.message || 'Failed to upload file. Please try again.');
    }
  },

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
};

export default departmentSubmissionService;